"use client"

import React from "react"
import { Search } from "lucide-react"

/** Small helper local to the modal */
function highlightSearch(text, query) {
  if (!query) return text
  try {
    const idxs = []
    const q = query.toLowerCase()
    let i = 0, hay = text.toLowerCase()
    while ((i = hay.indexOf(q, i)) !== -1) {
      idxs.push([i, i + q.length]); i += q.length
    }
    if (idxs.length === 0) return text
    const out = []
    let prev = 0
    idxs.forEach(([s, e], k) => {
      if (prev < s) out.push(<span key={`t-${k}-a`}>{text.slice(prev, s)}</span>)
      out.push(
        <span key={`t-${k}-b`} className="bg-yellow-200 text-gray-900 dark:bg-yellow-600/60 dark:text-gray-100">
          {text.slice(s, e)}
        </span>
      )
      prev = e
    })
    if (prev < text.length) out.push(<span key="t-end">{text.slice(prev)}</span>)
    return out
  } catch {
    return text
  }
}

function downloadText(name, text) {
  const blob = new Blob([text], { type: "text/plain" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = name
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * Logs modal (stateless)
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - datasetLabel: string
 * - latestRun: { id?: string, ts?: number } | null
 * - text: string // log body to display
 * - query: string
 * - onQueryChange: (v: string) => void
 */
export default function GraphLogsModal({
  open,
  onClose,
  datasetLabel,
  latestRun,
  text,
  query,
  onQueryChange,
}) {
  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative w-[95vw] max-w-4xl max-h-[85vh] bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0">
            <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
              Logs • {datasetLabel}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {latestRun?.id
                ? `${latestRun.id} • ${new Date(latestRun.ts || Date.now()).toLocaleString()}`
                : "No recent run"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2 top-2.5 text-gray-400 dark:text-gray-400" />
              <input
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                placeholder="Find in logs…"
                className="h-9 pl-8 pr-2 border rounded bg-white text-gray-900 min-w-[220px] border-gray-300 placeholder-gray-400
                           dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:placeholder-gray-400"
              />
            </div>
            <button
              onClick={() => navigator.clipboard?.writeText(text || "").catch(() => {})}
              className="h-9 px-3 rounded-md bg-gray-100 text-gray-900 hover:bg-gray-200
                         dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
            >
              Copy
            </button>
            <button
              onClick={() => downloadText(`${latestRun?.id || "run"}_logs.txt`, text || "")}
              className="h-9 px-3 rounded-md bg-gray-100 text-gray-900 hover:bg-gray-200
                         dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
            >
              Download
            </button>
            <button
              onClick={onClose}
              className="h-9 px-3 rounded-md bg-blue-600 text-white hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-0">
          <pre className="m-0 p-4 overflow-auto text-sm leading-6 whitespace-pre-wrap font-mono
                           bg-white text-gray-900
                           dark:bg-gray-900 dark:text-gray-100"
          >
            {highlightSearch(String(text || "No logs available for this run."), query)}
          </pre>
        </div>
      </div>
    </div>
  )
}
